'use client';

import { useMemo } from 'react';

import { useModal } from '@/components/ModalContext';
import { useProduct, getVariantBasedOnSelectedOptions } from '@/contexts/ProductContext';
import { usePricing } from '@/contexts/PricingContext';
import { formatPrice, toCents, toDollars } from '@/utils/utilities.mjs';
import {
  getShopifyOptionByName,
  BIRCH_COOLING_OPTION_NAME,
} from '@/components/blocks/variantOptions/shopifyOptionConventions';
import {
  getSelectedOptionValue,
  optionNamesMatch,
  optionValuesMatch,
} from '@/utils/product/product-context-helpers';
import rte from '@/utils/rte';
import PriceDetails from '@/components/global/PriceDetails';
import InfoIcon from '@/components/icons/InfoIcon';
import V2ExtendedOptionButtonsModal from './V2ExtendedOptionButtonsModal';

// BL-651: same pricing-diff logic as VariantButtons.js (V1's Cooling Options
// block) — duplicated rather than shared, same call V1 made for its own local
// helpers ("single consumer — no need to extract").
function getBaseVariant(variants, selectedVariant, optionName, pricing) {
  if (!variants?.length || !selectedVariant) return null;
  const slice = variants.filter((v) =>
    v.selectedOptions.every((o) =>
      optionNamesMatch(o.name, optionName)
        ? true
        : selectedVariant.selectedOptions.some(
            (s) => optionNamesMatch(s.name, o.name) && s.value === o.value,
          ),
    ),
  );
  if (!slice.length) return null;
  return slice.reduce((a, b) =>
    pricing.variant(a).sale <= pricing.variant(b).sale ? a : b,
  );
}

function getPriceDiff(variantA, variantB, pricing) {
  if (!variantA || !variantB) return { sale: undefined, full: undefined, hasDiff: false };
  const a = pricing.variant(variantA);
  const b = pricing.variant(variantB);
  if (a.sale == null || b.sale == null) return { sale: undefined, full: undefined, hasDiff: false };
  const saleCents = toCents(a.sale) - toCents(b.sale);
  const fullCents = toCents(a.full) - toCents(b.full);
  return { sale: toDollars(saleCents), full: toDollars(fullCents), hasDiff: saleCents !== fullCents };
}

// BL-651: the selected row reads "Selected" instead of a price, matching the
// Helix reference. Price diffs here are relative to the cheapest variant, so
// the base option rendered a meaningless "+$0" whether or not it was the one
// chosen — the label now says which option is active, which is what that
// column is actually for.
// Fixed width here (not just shrink-0) so "Selected" vs a price diff never
// changes this column's width — that width change was what shifted the wrap
// point of the description next to it and caused layout shift on selection.
function PriceLabel({ sale, full, hasDiff, isSelected }) {
  if (isSelected) {
    return (
      <span className="w-20 shrink-0 text-right text-sm font-semibold text-primary whitespace-nowrap">Selected</span>
    );
  }
  if (sale === undefined) return null;
  const prefix = sale < 0 ? '' : '+';
  return (
    <div className="flex w-20 shrink-0 flex-col text-right tabular-nums font-worksans">
      <span className="text-sm font-medium text-primary">{prefix}{formatPrice(sale)}</span>
      {hasDiff && full > 0 && (
        <span className="text-sm font-light line-through text-primary">{prefix}{formatPrice(full)}</span>
      )}
    </div>
  );
}

// BL-651: "Secondary Variant Options" in the ticket, `v2_extended_option_buttons`
// in the CMS — content matches Shopify's "Cooling Options" values, so this
// resolves against BIRCH_COOLING_OPTION_NAME. section_label is a display
// heading only, not the option-name matcher (unlike V1's section_title).
const V2ExtendedOptionButtons = ({ block }) => {
  const { selectedVariant, updateOptions, productData } = useProduct();
  const pricing = usePricing();
  const { openModal } = useModal();

  const {
    section_label: heading = '',
    section_description: sectionDescription,
    variant_options: rows = [],
    pitstop,
  } = block ?? {};

  const shopifyProduct = productData?.shopifyProduct;
  const shopifyOption = getShopifyOptionByName(shopifyProduct, BIRCH_COOLING_OPTION_NAME);
  const currentValue = getSelectedOptionValue(selectedVariant, BIRCH_COOLING_OPTION_NAME);
  const descriptionHtml = rte(sectionDescription);

  const baseVariant = useMemo(
    () => getBaseVariant(shopifyProduct?.variants, selectedVariant, BIRCH_COOLING_OPTION_NAME, pricing),
    [shopifyProduct?.variants, selectedVariant, pricing],
  );

  const renderedRows = useMemo(() => {
    if (!shopifyOption) return [];
    // BL-757: `variant_name` and the displayed label are now two different
    // things, and they have to stay that way. The CMS marks Variant Name
    // "REQUIRED. This must match EXACTLY what the variant option title value is
    // in Shopify" — it is the key this reduce matches against, so it keeps
    // driving `optionValuesMatch`. Only the rendered title falls back to it.
    // Swapping the custom label in as the matcher would make `find` miss, the
    // row would be dropped by the `return acc` below with no warning outside
    // dev, and a row that drops silently takes the whole block with it once
    // none are left (see the `renderedRows.length` guard). The real content
    // already differs — "Standard Luxe Breathable Layers" in Shopify vs
    // "Standard Breathable Layers" on screen — so this is not hypothetical.
    return rows.reduce((acc, {
      variant_name: matchName,
      custom_variant_label: customLabel,
      variant_description: description = '',
    }) => {
      const optionValue = shopifyOption.values.find((v) => optionValuesMatch(v, matchName));
      if (!optionValue) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[V2ExtendedOptionButtons] No Shopify match for CMS variant_name: "${matchName}".`);
        }
        return acc;
      }

      const isSelected = currentValue === optionValue;
      const matchingVariant = getVariantBasedOnSelectedOptions(
        BIRCH_COOLING_OPTION_NAME,
        optionValue,
        productData,
        selectedVariant,
      );
      const { sale, full, hasDiff } = getPriceDiff(matchingVariant, baseVariant, pricing);

      // `.trim()` so a field holding only whitespace counts as empty and falls
      // back, rather than rendering a blank label.
      acc.push({
        title: customLabel?.trim() || matchName,
        description,
        optionValue,
        isSelected,
        sale,
        full,
        hasDiff,
      });
      return acc;
    }, []);
  }, [rows, shopifyOption, productData, selectedVariant, pricing, baseVariant, currentValue]);

  if (!shopifyOption || !renderedRows.length || !selectedVariant) return null;

  return (
    <section className="flex flex-col gap-4 md:my-4 font-worksans text-primary" data-block="v2_extended_option_buttons">
      {/* BL-801: heading and its description are one group — the section's
          gap-4 belongs between the group and the option cards, not between
          these two lines, which Figma sets flush against each other. */}
      {heading || descriptionHtml ? (
        <div>
          {heading ? <p className="text-xl font-medium leading-[1.5]">{heading}</p> : null}

          {descriptionHtml ? (
            <div
              className="text-base font-light leading-[1.5] [&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          ) : null}
        </div>
      ) : null}

      <fieldset className="grid gap-2.5 border-0 p-0">
        <legend className="sr-only">{heading || BIRCH_COOLING_OPTION_NAME}</legend>
        {renderedRows.map(({ title, description, optionValue, isSelected, sale, full, hasDiff }) => (
          <label
            key={optionValue}
            // BL-781: 1px sand at rest, the same as the size selector above it —
            // these were 2px sand-light, thicker and a shade lighter, which is
            // what made the two modules read as different controls. The border
            // width no longer changes between states: the selected ring is an
            // overlay below, so selecting an option cannot move the card. Doing
            // it per-branch instead grew the card 2px and shifted its contents
            // 1px on every change, which is the reason the size selector was
            // built this way in the first place.
            // BL-808: hover thickens the edge instead of flooding the card.
            // `ring-inset` in the border's own colour sits against the 1px
            // border and reads as it doubling, which is what the size selector
            // beside it already does and what the Helix buy box these were
            // modelled on does: `hover:ring-1 hover:ring-inset
            // hover:ring-[#C9C9C9]`, no fill. The beige flood came from Birch's
            // V1 selector, which is still what birch-living.vercel.app serves.
            // BL-790: asymmetric padding (16px horizontal / 24px vertical) per Figma spec, not the uniform 20px used elsewhere.
            className={`relative flex cursor-pointer border bg-white px-4 py-6 outline-none transition-colors duration-150 has-focus-visible:ring-2 has-focus-visible:ring-sea has-focus-visible:ring-offset-2 ${
              isSelected ? 'border-sea' : 'border-sand hover:ring-1 hover:ring-inset hover:ring-sand'
            }`}
          >
            <input
              type="radio"
              name={`variant-${BIRCH_COOLING_OPTION_NAME}`}
              value={optionValue}
              checked={isSelected}
              onChange={() =>
                updateOptions([{ name: BIRCH_COOLING_OPTION_NAME, value: optionValue }], { persist: true })
              }
              className="sr-only"
            />
            <div className="flex w-full justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm sm:text-base font-medium leading-5">{title}</div>
                {description ? (
                  <div className="pt-1.5 text-sm font-light leading-5 text-primary/70">
                    {description}
                  </div>
                ) : null}
              </div>
              <PriceLabel sale={sale} full={full} hasDiff={hasDiff} isSelected={isSelected} />
            </div>
            {/* BL-781: the selected ring, overlaid rather than a border-width
                change, so the card holds its size and its contents stay put
                when the selection moves. Same technique and same 2.5px as the
                size selector, and the label border switches to sea underneath
                so the two edges never read as separate rings. */}
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute -inset-px border-[2.5px] ${
                isSelected ? 'border-sea' : 'border-transparent'
              }`}
            />
          </label>
        ))}
      </fieldset>

      {/* BL-651: boxed, clickable pitstop card below the variant buttons —
          heading_label + description visible on the card itself, not hidden
          behind the modal (that was mixing them with the modal's own deeper
          content, which the review flagged). Matches Helix's V2 reference. */}
      {pitstop?.heading_label && (
        <button
          type="button"
          onClick={() => openModal(<V2ExtendedOptionButtonsModal pitstop={pitstop} />, null, 'bg-white')}
          className="flex items-start justify-between gap-4 bg-warm-white p-4 text-left cursor-pointer border-sand border group"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium leading-5">{pitstop.heading_label}</div>
            {pitstop.description && (
              <div className="pt-1.5 text-sm font-light leading-5 text-primary">
                {pitstop.description}
              </div>
            )}
          </div>
          <InfoIcon className="size-4.5 shrink-0 text-primary" />
        </button>
      )}

      {/* self-start so the link sits at the left edge of the block, as in the
          reference — PriceDetails renders a full-width button, which centres
          its own text when stretched by the flex column. */}
      <PriceDetails className="self-start text-left" />
    </section>
  );
};

export default V2ExtendedOptionButtons;
